import React, { useEffect, useState, useRef } from "react";
import { GooglePlaceAutocomplete, SelectInput } from '@hometown/react-hometown-lib';
import { formatBookingJson, getFormDataForStorage, createAddressEvent, manualGoogleEvents } from '../helpers/helpers';
import CircularProgress from '@mui/material/CircularProgress';

const wasteOptions = [
    { value: "1", label: "Trash" },
    { value: "2", label: "Construction Debris" },
    { value: "3", label: "Concrete" },
    { value: "4", label: "Roofing" },
    { value: "5", label: "Yard Waste" },
    { value: "6", label: "Dirt" },
    { value: "7", label: "Rock or Brick" },
];

const IconUser = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
        <circle cx="12" cy="7" r="4"/>
    </svg>
);

const IconBriefcase = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
    </svg>
);

const buildGooglePlaceFromAddress = (addressData) => {
    if (!addressData?.odf_postal_code) return '';
    return JSON.stringify(
        Object.entries(addressData)
            .filter(([, v]) => v)
            .map(([key, value]) => [key.replace('odf_', ''), value])
    );
};

const KppSidebarSearchForm = ({ place }) => {
    const dataFromStorage = getFormDataForStorage();
    const [googlePlace, setGooglePlace] = useState(buildGooglePlaceFromAddress(dataFromStorage?.addressData));
    const [selectedWasteType, setSelectedWasteType] = useState(dataFromStorage?.wasteType || '');
    const [formValues, setFormValues] = useState(dataFromStorage?.addressData ? { ...dataFromStorage.addressData } : {});
    // 09/09: no longer defaults to 'homeowner' -- per design review, forcing an explicit choice
    // is worth the extra click for homeowners because it stops contractors from being silently
    // lumped in with the majority segment. Still respects a real prior choice from localStorage.
    const [userType, setUserType] = useState(null);
    const [addressFocused, setAddressFocused] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isError, setIsError] = useState(false);
    const [isAddressError, setIsAddressError] = useState(false);
    const [isUserTypeError, setIsUserTypeError] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const addressFieldRef = useRef(null);
    const [shouldMountAutocomplete, setShouldMountAutocomplete] = useState(false);

    // Defer loading the Google Maps/Places script (and its non-trivial main-thread cost) until
    // the address field is visible AND the browser is idle. On mobile the field is hidden
    // (display:none) until the sidebar expands, so this naturally pushes the cost past
    // interaction time; on desktop it's visible immediately, so only the idle-wait applies.
    useEffect(() => {
        const node = addressFieldRef.current;
        if (!node) return;

        // Being visible isn't enough on its own (desktop is visible from the first frame) — wait
        // for the browser to report an idle moment too, so the Maps script load lands after the
        // critical initial render instead of competing with it. The timeout guarantees it still
        // mounts promptly even under heavy main-thread load.
        const scheduleMount = () => {
            const runWhenIdle = window.requestIdleCallback
                ? (cb) => window.requestIdleCallback(cb, { timeout: 1000 })
                : (cb) => setTimeout(cb, 200);
            runWhenIdle(() => setShouldMountAutocomplete(true));
        };

        const observer = new IntersectionObserver((entries) => {
            if (entries[0]?.isIntersecting) {
                scheduleMount();
                observer.disconnect();
            }
        });
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const stored = localStorage.getItem('userType');
        if (stored) setUserType(stored);
    }, []);

    useEffect(() => {
        if (googlePlace) return;
        const data = getFormDataForStorage();
        if (data?.addressData?.odf_postal_code) {
            setGooglePlace(buildGooglePlaceFromAddress(data.addressData));
            setFormValues({ ...data.addressData });
        }
    }, []);

    useEffect(() => {
        try {
            const stored = JSON.parse(localStorage.getItem('onlineBookingData') || 'null');
            if (
                stored?.finalDataForBooking?.find(e => e.key === 'fullFormData')?.value ||
                stored?.initialValues?.find(e => e.key === 'fullFormData')?.value
            ) {
                setAddressFocused(true);
                return;
            }
        } catch {}
        const input = document.querySelector('.address-ondeff input');
        if (input?.value) setAddressFocused(true);
    }, []);

    useEffect(() => {
        const element = document.getElementById("kpp-sidebar-waste-select");
        if (!element) return;
        if (selectedWasteType) element.classList.add("select-shrink");
        else element.classList.remove("select-shrink");
    }, [selectedWasteType, isExpanded]);

    useEffect(() => {
        const handler = (e) => {
            setSelectedWasteType(e.detail.wasteType);
            setIsExpanded(true);
        };
        document.addEventListener('kpp:preselect-waste', handler);
        return () => document.removeEventListener('kpp:preselect-waste', handler);
    }, []);

    // Refresh from whatever just got persisted (e.g. the "Get Pricing & Availability" popup),
    // since this sidebar only reads onlineBookingData once at mount otherwise.
    useEffect(() => {
        const handler = (e) => {
            const addressData = e.detail || {};
            if (addressData.odf_postal_code) {
                setFormValues({ ...addressData });
                setGooglePlace(buildGooglePlaceFromAddress(addressData));
            }
            if (addressData.wasteType) setSelectedWasteType(addressData.wasteType);
        };
        document.addEventListener('onlineBookingData:updated', handler);
        return () => document.removeEventListener('onlineBookingData:updated', handler);
    }, []);

    useEffect(() => {
        let lastSetTop = null;
        const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                if (mutation.attributeName !== 'style') continue;
                if (!mutation.target.classList?.contains('pac-container')) continue;
                const pac = mutation.target;
                if (!pac.style.top) continue;
                const rawTop = parseFloat(pac.style.top);
                if (isNaN(rawTop)) continue;
                if (lastSetTop !== null && Math.abs(rawTop - lastSetTop) < 1) {
                    lastSetTop = null;
                    continue;
                }
                // Reposition relative to whichever address input is actually focused (the one
                // that triggered this dropdown), not a fixed selector: a KPP/PPP page can have
                // more than one Google-autocomplete address field mounted at once (this sidebar
                // AND the "Get Pricing & Availability" popup both use .address-ondeff), so a
                // fixed selector can grab the wrong one and place the dropdown next to it instead.
                const focused = document.activeElement;
                const input = focused?.closest('.google-place-autocomplete')?.querySelector('input')
                    || document.querySelector('.address-ondeff input');
                if (!input) continue;
                const rect = input.getBoundingClientRect();
                lastSetTop = rect.bottom + 4;
                pac.style.top = `${lastSetTop}px`;
                pac.style.left = `${rect.left}px`;
                pac.style.width = `${rect.width}px`;
            }
        });
        observer.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['style'] });
        return () => observer.disconnect();
    }, []);

    const handleWasteChange = (wasteType) => {
        if (wasteType) {
            setIsError(false);
            setSelectedWasteType(wasteType);
            if (formValues.odf_postal_code) formatBookingJson({ ...formValues, wasteType });
        }
        else setIsError(true);
    };

    const handleUserTypeChange = (type) => {
        setUserType(type);
        setIsUserTypeError(false);
        localStorage.setItem('userType', type);
    };

    const handleAddressChange = (placeData) => {
        setIsAddressError(false);
        setGooglePlace(JSON.stringify(placeData));
        let addressValues = {};
        placeData.map((value) => { addressValues["odf_" + value[0]] = value[1]; });
        createAddressEvent({
            street1: addressValues.odf_street,
            street2: addressValues.odf_street2,
            city: addressValues.odf_locality,
            zipCode: addressValues.odf_postal_code,
            state: addressValues.odf_state,
        });
        setFormValues({ ...addressValues });
        if (selectedWasteType) formatBookingJson({ ...addressValues, wasteType: selectedWasteType });
    };

    const handleSubmit = async () => {
        const missingAddress = !formValues.odf_postal_code;
        const missingWaste = !selectedWasteType;
        const missingUserType = !userType;

        setIsAddressError(missingAddress);
        setIsError(missingWaste);
        setIsUserTypeError(missingUserType);

        if (missingAddress || missingWaste || missingUserType) {
            // Per design review: button stays active/clickable the whole time -- jump to the
            // first missing field instead of graying the button out with no explanation.
            if (missingAddress) {
                document.getElementById('kpp-sidebar-address')?.focus();
            } else if (missingWaste) {
                document.getElementById('kpp-sidebar-waste-type-select')?.focus();
            } else if (missingUserType) {
                document.querySelector('.kpp-sidebar-search__user-type-btn')?.focus();
            }
            return;
        }

        try {
            setIsSubmitting(true);
            formatBookingJson({ ...formValues, wasteType: selectedWasteType });
            localStorage.setItem('userType', userType);
            manualGoogleEvents('manualFormSubmition');
            window.location.href = '/book';
        } catch (error) {
            console.log('error', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const addressFilled = Object.keys(formValues).length > 0 || addressFocused;

    return (
        <div id="kpp-sidebar-search" className={`kpp-sidebar-search${addressFilled ? ' address-filled' : ''}${isExpanded ? ' kpp-sidebar-search--expanded' : ''}`} onClick={!isExpanded ? () => setIsExpanded(true) : undefined}>
            {/* Collapsed header — CSS shows this on mobile by default, hidden on desktop */}
            <div className="kpp-sidebar-search__collapsed">
                <div>
                    <h3 className="kpp-sidebar-search__title" style={{ marginBottom: 0 }}>Compare dumpster rental rates in {place?.name}</h3>
                </div>
                <svg className="kpp-sidebar-search__expand-icon" xmlns="http://www.w3.org/2000/svg" width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            </div>
            {/* Full form — CSS shows this always on desktop, on mobile only when --expanded */}
            <div className="kpp-sidebar-search__form-content">
                <div className="kpp-sidebar-search__title-row" onClick={() => { if (isExpanded) setIsExpanded(false); }} style={isExpanded ? { cursor: 'pointer' } : undefined}>
                    <h3 className="kpp-sidebar-search__title" style={{ marginBottom: addressFilled ? '25px' : '8px', transition: 'margin-bottom 0.2s ease' }}>Compare dumpster rental rates in {place?.name}</h3>
                    <button className="kpp-sidebar-search__collapse-btn" onClick={(e) => { e.stopPropagation(); setIsExpanded(false); }} type="button">
                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
                    </button>
                </div>
                <div className="kpp-sidebar-search__form">
                    <div
                        className="kpp-sidebar-search__field address-ondeff"
                        ref={addressFieldRef}
                        onFocus={() => setAddressFocused(true)}
                        onBlur={() => setAddressFocused(false)}
                    >
                        {shouldMountAutocomplete ? (
                            <GooglePlaceAutocomplete
                                key={googlePlace ? 'filled' : 'empty'}
                                id="kpp-sidebar-address"
                                name="address"
                                label="Enter delivery address"
                                fieldName="address"
                                placeholder=""
                                apiKey={`${process.env.GATSBY_BOOKABLE_API_AUTOCOMPLETE}`}
                                className="fill-available"
                                resetError={() => setIsAddressError(false)}
                                onResetField={() => { setGooglePlace(''); setFormValues({}); setIsAddressError(false); }}
                                onChange={(placeData) => handleAddressChange(placeData)}
                                onChangeValue={googlePlace}
                                formValues={formValues}
                                fullWidth
                                alwaysShrink={!!formValues?.odf_postal_code}
                            />
                        ) : (
                            // Reserves the same footprint as the MUI outlined field above (22px
                            // wrapper padding-top + 56px standard input height) so mounting the
                            // real widget a frame later doesn't shift the layout.
                            <div style={{ height: '56px' }} aria-hidden="true" />
                        )}
                        {isAddressError && <p className="kpp-sidebar-search__field-error">Please enter a valid delivery address.</p>}
                    </div>
                    <div id="kpp-sidebar-waste-select" className="kpp-sidebar-search__field select-ondeff outlined-select">
                        <SelectInput
                            id="kpp-sidebar-waste-type-select"
                            className="outlined-select"
                            value={selectedWasteType}
                            name="wasteType"
                            label="Select your waste"
                            data={wasteOptions}
                            onChange={handleWasteChange}
                            error="Please select a waste type from the list."
                            isError={isError}
                            fullWidth
                            MenuProps={{
                                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                transformOrigin: { vertical: 'top', horizontal: 'left' },
                            }}
                        />
                    </div>
                    <div className="kpp-sidebar-search__user-type-label">
                        I'm a:
                        {!userType && <span className="kpp-sidebar-search__user-type-hint">Selection required</span>}
                    </div>
                    <div className="kpp-sidebar-search__user-type">
                        <button
                            className={`kpp-sidebar-search__user-type-btn${userType === 'homeowner' ? ' active' : ''}${isUserTypeError ? ' has-error' : ''}`}
                            onClick={() => handleUserTypeChange('homeowner')}
                            type="button"
                        >
                            <IconUser /> Homeowner
                        </button>
                        <button
                            className={`kpp-sidebar-search__user-type-btn${userType === 'business' ? ' active' : ''}${isUserTypeError ? ' has-error' : ''}`}
                            onClick={() => handleUserTypeChange('business')}
                            type="button"
                        >
                            <IconBriefcase /> Contractor/Business
                        </button>
                    </div>
                    {isUserTypeError &&
                        <p className="kpp-sidebar-search__user-type-error">Please select whether you are a Homeowner or Contractor/Business.</p>
                    }
                    <div className="kpp-sidebar-search__submit-wrapper">
                        <button
                            className="kpp-sidebar-search__submit"
                            onClick={handleSubmit}
                            type="button"
                        >
                            {isSubmitting
                                ? <CircularProgress style={{ color: '#FFF', width: 24, height: 24 }} />
                                : 'Search instant prices'
                            }
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default KppSidebarSearchForm;
